import api from '@/lib/axios';
import { downloadExport, ExportFormat, Staff } from '@/services/school.api';
import type { PaginatedResult } from '@/components/data-table';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type LoginReportView = 'never' | 'last';

export interface LoginReportRow {
  userId: string;
  name: string;
  email: string | null;
  role: string;
  lastLoginAt: string | null;
  lastLoginType: string | null;
}

export interface LoginReportParams {
  view: LoginReportView;
  role?: string;
  page?: number;
  limit?: number;
}

export interface StudentSummaryRow {
  classId: string;
  className: string;
  sectionId: string;
  sectionName: string;
  total: number;
  male: number;
  female: number;
  other: number;
  active: number;
  left: number;
}

export interface StudentSummaryTotals {
  total: number;
  male: number;
  female: number;
  other: number;
  active: number;
  left: number;
}

export interface StudentSummaryReport {
  academicYearId: string | null;
  rows: StudentSummaryRow[];
  totals: StudentSummaryTotals;
}

export interface StudentPortfolioBasic {
  id: string;
  admissionNo: string;
  studentName: string;
  photoUrl: string | null;
  rollNumber: number | null;
  gender: string | null;
  dob: string | null;
  mobile: string | null;
  email: string | null;
  username: string | null;
  schoolName: string;
  className: string;
  sectionName: string;
  mediumName: string | null;
  sessionYearName: string | null;
  admissionDate: string;
  currentAddress: string | null;
  permanentAddress: string | null;
  enrollmentStatus: 'continue' | 'left' | null;
}

export interface StudentPortfolioGuardian {
  name: string;
  mobile: string | null;
  email: string | null;
  gender: string | null;
  photoUrl: string | null;
}

export interface StudentPortfolioAttendance {
  present: number;
  absent: number;
  late: number;
  holiday: number;
  halfDay: number;
  percentage: number | null;
}

export interface StudentPortfolioFeeDemand {
  id: string;
  feeStructureName: string;
  installmentName: string | null;
  dueDate: string | null;
  actualAmount: number;
  discountAmount: number;
  paidAmount: number;
  balance: number;
}

export interface StudentPortfolioPayment {
  id: string;
  feeStructureName: string;
  installmentName: string | null;
  amount: number;
  paymentMethod: string | null;
  paymentReference: string | null;
  paidDate: string | null;
}

export interface StudentPortfolioFees {
  demanded: number;
  paid: number;
  pending: number;
  demands: StudentPortfolioFeeDemand[];
  payments: StudentPortfolioPayment[];
}

export interface StudentPortfolio {
  student: StudentPortfolioBasic;
  guardian: StudentPortfolioGuardian | null;
  attendance: StudentPortfolioAttendance | null;
  fees: StudentPortfolioFees | null;
}

export interface FinanceReportRow {
  schoolName: string;
  academicYearId: string | null;
  academicYearName: string | null;
  month: number;
  totalIncome: number;
  totalExpense: number;
  balance: number;
}

export interface FinanceReportParams {
  academicYearId?: string;
}

export interface VideoViewReportRow {
  id: string;
  subjectName: string;
  lessonName: string;
  topicName: string | null;
  videoName: string;
  totalViews: number;
  totalDuration: string | null;
  watchedDuration: string | null;
  watchedPercentage: number | null;
  lastWatched: string | null;
  applicableStudents: number;
}

export interface VideoViewReportParams {
  classId?: string;
  subjectId?: string;
}

/** Fetch a ZIP (with auth headers) and trigger a browser download — same shape as downloadExport() but no `format` query param (always zip). */
async function downloadZip(path: string, filename: string): Promise<void> {
  const res = await api.get(path, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: 'application/zip' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export const ReportsApi = {
  loginReport: async (params: LoginReportParams): Promise<PaginatedResult<LoginReportRow>> =>
    unwrap(await api.get('/reports/login', { params })),

  exportStudents: (format: ExportFormat) =>
    downloadExport('/reports/export/students', 'students-roster', format),

  exportStaff: (format: ExportFormat) =>
    downloadExport('/reports/export/staff', 'staff-roster', format),

  /** Student photo ZIP — matches the real legacy "Download Images" button. Throws if no student has an uploaded photo yet. */
  exportStudentImages: () => downloadZip('/reports/export/students/images', 'student-photos.zip'),

  /** Teacher photo ZIP — matches the real legacy "Download Images" button. Throws if no teacher has an uploaded photo yet. */
  exportTeacherImages: () => downloadZip('/reports/export/teachers/images', 'teacher-photos.zip'),

  studentSummary: async (params?: { academicYearId?: string; classId?: string }): Promise<StudentSummaryReport> =>
    unwrap(await api.get('/reports/students-summary', { params })),

  studentPortfolio: async (studentId: string): Promise<StudentPortfolio> =>
    unwrap(await api.get(`/reports/students/${studentId}/portfolio`)),

  teachersReport: async (params?: { page?: number; limit?: number; search?: string; department?: string }): Promise<PaginatedResult<Staff>> =>
    unwrap(await api.get('/reports/teachers', { params })),

  exportTeachers: (format: ExportFormat) =>
    downloadExport('/reports/teachers/export', 'teachers-report', format),

  financeReport: async (params?: FinanceReportParams): Promise<FinanceReportRow[]> =>
    unwrap(await api.get('/reports/finance', { params })),

  exportFinanceReport: (format: ExportFormat, params?: FinanceReportParams) =>
    downloadExport('/reports/finance/export', 'finance-report', format, params as Record<string, unknown>),

  videoViewReport: async (params?: VideoViewReportParams): Promise<VideoViewReportRow[]> =>
    unwrap(await api.get('/reports/video-view', { params })),

  exportVideoViewReport: (format: ExportFormat, params?: VideoViewReportParams) =>
    downloadExport('/reports/video-view/export', 'video-view-report', format, params as Record<string, unknown>),
};
